import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useMatchmakingStateManagement } from '../hooks/useMatchmakingStateManagement';
import { useMatchmakingData } from '../hooks/useMatchmakingData';
import { useMatchmakingHandlers } from '../hooks/useMatchmakingHandlers';
import { useMatchmakingProps } from '../hooks/useMatchmakingProps';
import { ProDashboardMatchmakingModals } from './ProDashboardMatchmakingModals';
import { ProDashboardMatchmakingContent } from './ProDashboardMatchmakingContent';
import { GenericSearchTab } from './GenericSearchTab';
import type { StepperAnswers } from '../hooks/useMatchmakingStateManagement';
import { BetaBanner } from '@/shared/components/BetaBanner';

type TabType = 'client' | 'generic';

const ProDashboardMatchmaking = () => {
  const { user: loggedInUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('client');
  const genericHandleFindMatchesRef = useRef<((answers: StepperAnswers) => void) | null>(null);
  const navigate = useNavigate();

  const state = useMatchmakingStateManagement();
  const {
    isDrawerOpen,
    setDrawerOpen,
    isClientModalOpen,
    setClientModalOpen,
    isLoading,
    isLoadingMore,
    error,
    showNoProvidersFound,
    noProviderMessage,
    loadMoreMessage,
    isHistoryPopoverOpen,
    setHistoryPopoverOpen,
    showHistoryTooltip,
    setShowHistoryTooltip,
    isLocationModalOpen,
    setIsLocationModalOpen,
    isViewingHistoryResults,
    isSupportModalOpen,
    setIsSupportModalOpen,
    isHistoryDetailsModalOpen,
    setIsHistoryDetailsModalOpen,
    selectedAddress,
    setSelectedAddress,
    selectedLatitude,
    selectedLongitude,
    selectedState,
    selectedCounty,
    selectedZipCode,
    addressResetTrigger,
    shouldFetchHistory,
    setShouldFetchHistory,
    currentSearchId,
    stepperAnswers,
    setStepperAnswers,
    lastHistoryItemRef,
    lastPromptRef,
  } = state;

  const {
    clients,
    clientsError,
    clientsLoading,
    selectedClientId,
    setSelectedClientId,
    advisorProfileId,
    selectedClient,
    hasClients,
    currentLoadingMessageIndex,
    currentLoadingTheme,
    matchmakingState,
    matchingPrompt,
    hasMore,
    isPreloadingImages,
    preloadedImages,
    imagePreloadingMessageIndex,
    currentImagePreloadingTheme,
    userClearedAddressRef,
    handleAddressSelectAndGeocode,
    clientProfile,
    ageDisplay,
    matchResults,
    hasMatches,
    shouldShowPostSingleLoadExhaustedMessage,
    shouldShowPostSecondLoadMessage,
    cheerfulMessage,
    fetchHistoryResults,
    executeMatchSearch,
    handleLoadMore,
  } = useMatchmakingData({ state });

  const { handleGenerateMatches, handleFindMatches, handlePromptChange, handleSelectHistory } =
    useMatchmakingHandlers({
      matchingPrompt,
      hasClients,
      selectedClientId,
      isViewingHistoryResults,
      lastPromptRef,
      lastHistoryItemRef,
      setIsLocationModalOpen,
      fetchHistoryResults,
      executeMatchSearch,
      setStepperAnswers,
      matchmakingState,
      selectedClientIdValue: selectedClientId,
    });

  const { clientAvatar, modalProps } = useMatchmakingProps({
    loggedInUser,
    selectedClient,
    clientProfile,
    ageDisplay,
    matchingPrompt,
    selectedZipCode,
    selectedState,
    selectedCounty,
    selectedAddress,
    lastHistoryItemRef,
  });

  if (!loggedInUser) return <h3>Please log in to see matchmaking.</h3>;

  return (
    <div className="container mx-auto min-h-screen flex flex-col py-8 px-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">bQuest Navigator</h2>
      <BetaBanner className="mb-6" />

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('client')}
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeTab === 'client'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          My Clients
        </button>
        <button
          onClick={() => setActiveTab('generic')}
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeTab === 'generic'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Generic Search
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'client' ? (
        <ProDashboardMatchmakingContent
        clients={clients}
        selectedClientId={selectedClientId}
        setSelectedClientId={setSelectedClientId}
        isDrawerOpen={isDrawerOpen}
        setDrawerOpen={setDrawerOpen}
        selectedClient={selectedClient}
        clientsLoading={clientsLoading}
        hasClients={hasClients}
        matchingPrompt={matchingPrompt}
        onPromptChange={handlePromptChange}
        onGenerateMatches={() => handleGenerateMatches(false)}
        isLoading={isLoading}
        isHistoryPopoverOpen={isHistoryPopoverOpen}
        setHistoryPopoverOpen={setHistoryPopoverOpen}
        showHistoryTooltip={showHistoryTooltip}
        setShowHistoryTooltip={setShowHistoryTooltip}
        onSelectHistory={(historyItem) => handleSelectHistory(historyItem, advisorProfileId)}
        advisorId={advisorProfileId}
        clientId={selectedClientId}
        shouldFetchHistory={shouldFetchHistory && !!advisorProfileId}
        onFetchHistoryComplete={() => setShouldFetchHistory(false)}
        clientsError={clientsError}
        error={error}
        showNoProvidersFound={showNoProvidersFound}
        noProviderMessage={noProviderMessage}
        hasMatches={hasMatches}
        isPreloadingImages={isPreloadingImages}
        currentLoadingTheme={currentLoadingTheme}
        currentLoadingMessageIndex={currentLoadingMessageIndex}
        currentImagePreloadingTheme={currentImagePreloadingTheme}
        imagePreloadingMessageIndex={imagePreloadingMessageIndex}
        matchResults={matchResults}
        preloadedImages={preloadedImages}
        currentSearchId={currentSearchId}
        isViewingHistoryResults={isViewingHistoryResults}
        onHistoryDetailsClick={() => setIsHistoryDetailsModalOpen(true)}
        cheerfulMessage={cheerfulMessage}
        shouldShowPostSingleLoadExhaustedMessage={shouldShowPostSingleLoadExhaustedMessage}
        shouldShowPostSecondLoadMessage={shouldShowPostSecondLoadMessage}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMoreMessage={loadMoreMessage}
        onLoadMore={handleLoadMore}
        onContactUs={() => setIsSupportModalOpen(true)}
        onFindProviders={() => navigate('/search-providers')}
      />
      ) : (
        <GenericSearchTab
          state={state}
          advisorProfileId={advisorProfileId}
          handleFindMatchesRef={genericHandleFindMatchesRef}
        />
      )}

      <ProDashboardMatchmakingModals
        isClientModalOpen={isClientModalOpen}
        setClientModalOpen={setClientModalOpen}
        clientProfile={clientProfile}
        clientAvatar={clientAvatar}
        ageDisplay={ageDisplay}
        isLocationModalOpen={isLocationModalOpen}
        setIsLocationModalOpen={setIsLocationModalOpen}
        userClearedAddressRef={userClearedAddressRef}
        onFindMatches={activeTab === 'generic' && genericHandleFindMatchesRef.current
          ? genericHandleFindMatchesRef.current
          : handleFindMatches}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        onAddressSelectAndGeocode={handleAddressSelectAndGeocode}
        addressResetTrigger={addressResetTrigger}
        selectedState={selectedState}
        selectedCounty={selectedCounty}
        selectedZipCode={selectedZipCode}
        selectedLatitude={selectedLatitude}
        selectedLongitude={selectedLongitude}
        stepperAnswers={stepperAnswers}
        setStepperAnswers={setStepperAnswers}
        isSupportModalOpen={isSupportModalOpen}
        setIsSupportModalOpen={setIsSupportModalOpen}
        advisorEmail={modalProps.advisorEmail}
        advisorName={modalProps.advisorName}
        searchQuery={modalProps.searchQuery}
        locationZipCode={modalProps.locationZipCode}
        locationState={modalProps.locationState}
        locationCounty={modalProps.locationCounty}
        locationAddress={modalProps.locationAddress}
        isHistoryDetailsModalOpen={isHistoryDetailsModalOpen}
        setIsHistoryDetailsModalOpen={setIsHistoryDetailsModalOpen}
        historyItem={lastHistoryItemRef.current}
      />
    </div>
  );
};

export default ProDashboardMatchmaking;
